import {
  queryOptions,
  useQueries,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';
import { shallowEqualArrays } from '@listening/utils/ts-utils';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  enabledLegacyChapterChunks,
  legacyChapterChunkParams,
  type LegacyChapterChunkParams,
} from './chapter-chunks';

export const LEGACY_CHAPTER_CHUNKS_BEHIND = 1;
export const LEGACY_CHAPTER_CHUNKS_AHEAD = 1;
export const LEGACY_CHAPTER_CHUNK_GC_MS = 10 * 60_000;
export const LEGACY_CHAPTER_VISIBILITY_DEBOUNCE_MS = 500;

export type LegacyChapterChunkFetch<TPage> = (
  params: LegacyChapterChunkParams,
) => Promise<TPage>;

export const legacyChapterChunkQueryKey = (params: LegacyChapterChunkParams) =>
  ['text-extraction-data', params] as const;

/**
 * Drop every cached chunk of one document. A chunk is a word-level tree
 * (hundreds of bytes per word) that `gcTime` would otherwise keep for ten
 * minutes after the reader closes — across documents, that is what fills a
 * phone's memory budget. Reopening within that window simply refetches.
 */
export const removeLegacyChapterChunks = (
  queryClient: QueryClient,
  audioId: string,
) =>
  queryClient.removeQueries({
    queryKey: ['text-extraction-data', { audioId }],
  });

export const legacyChapterChunkQueryOptions = <TPage>(
  params: LegacyChapterChunkParams,
  fetchChunk: LegacyChapterChunkFetch<TPage>,
) =>
  queryOptions({
    queryKey: legacyChapterChunkQueryKey(params),
    queryFn: () => fetchChunk(params),
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: LEGACY_CHAPTER_CHUNK_GC_MS,
  });

export type LegacyChapterChunkState<TPage> = {
  status: 'idle' | 'pending' | 'error' | 'success';
  /** No data yet: outside the window, or in flight. */
  isPending: boolean;
  isFetching: boolean;
  isError: boolean;
  error: unknown;
  data: TPage | undefined;
};

const IDLE_CHUNK = {
  status: 'idle',
  isPending: true,
  isFetching: false,
  isError: false,
  error: null,
  data: undefined,
} as const satisfies LegacyChapterChunkState<never>;

const sameChunkState = <TPage>(
  a: LegacyChapterChunkState<TPage>,
  b: LegacyChapterChunkState<TPage>,
) =>
  a.status === b.status &&
  a.isFetching === b.isFetching &&
  a.isError === b.isError &&
  a.error === b.error &&
  a.data === b.data;

const sortMembers = <T extends string | number>(values: Iterable<T>) =>
  [...new Set(values)].toSorted((a, b) => (a < b ? -1 : a > b ? 1 : 0));

export function useDebouncedAnchors<T extends string | number>(
  delayMs = LEGACY_CHAPTER_VISIBILITY_DEBOUNCE_MS,
) {
  const [value, setValue] = useState<ReadonlyArray<T>>([]);
  const latestRef = useRef<ReadonlyArray<T>>([]);
  const pendingRef = useRef<ReadonlyArray<T> | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (timerRef.current !== null) clearTimeout(timerRef.current);
    },
    [],
  );
  const flush = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (pendingRef.current !== null) {
      setValue(pendingRef.current);
      pendingRef.current = null;
    }
  }, []);
  const update = useCallback(
    (next: Iterable<T>) => {
      const sorted = sortMembers(next);
      if (shallowEqualArrays(sorted, latestRef.current)) return;
      latestRef.current = sorted;
      pendingRef.current = sorted;
      if (timerRef.current !== null) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        pendingRef.current = null;
        setValue(sorted);
      }, delayMs);
    },
    [delayMs],
  );
  return { value, update, flush };
}

export type UseLegacyChapterChunksOptions<TPage> = {
  audioId: string;
  chapters: ReadonlyArray<{ id: string }>;
  fetchChunk: LegacyChapterChunkFetch<TPage>;
  enabled?: boolean;
  currentChapterIndex: number | undefined;
  /** A playing chapter for this document is still being resolved. The rows on
   * screen are wherever the list mounted, so anchoring on them now would load a
   * window playback is about to move away from. */
  currentChapterPending?: boolean;
  visibleChapterIndices: ReadonlyArray<number>;
  /** Web pins chunk 0 because its SSR loader prefetches it. */
  alwaysFirstChunk: boolean;
  chunkSize?: number;
};

export function useLegacyChapterChunks<TPage>({
  audioId,
  chapters,
  fetchChunk,
  enabled = true,
  currentChapterIndex,
  currentChapterPending = false,
  visibleChapterIndices,
  alwaysFirstChunk,
  chunkSize,
}: UseLegacyChapterChunksOptions<TPage>) {
  const anchorChapterIndices = useMemo(() => {
    const visible = currentChapterPending ? [] : visibleChapterIndices;
    return sortMembers(
      currentChapterIndex === undefined || currentChapterIndex < 0
        ? visible
        : [...visible, currentChapterIndex],
    );
  }, [currentChapterIndex, currentChapterPending, visibleChapterIndices]);
  const chunkParams = useMemo(
    () => legacyChapterChunkParams(audioId, chapters, chunkSize),
    [audioId, chapters, chunkSize],
  );
  const observedChunkIndices = useMemo(
    () =>
      sortMembers(
        enabledLegacyChapterChunks({
          chunkCount: chunkParams.length,
          anchorChapterIndices,
          behind: LEGACY_CHAPTER_CHUNKS_BEHIND,
          ahead: LEGACY_CHAPTER_CHUNKS_AHEAD,
          alwaysFirstChunk,
          ...(chunkSize === undefined ? {} : { chunkSize }),
        }),
      ),
    [alwaysFirstChunk, anchorChapterIndices, chunkParams.length, chunkSize],
  );
  const queryClient = useQueryClient();
  const observedQueries = useQueries({
    queries: observedChunkIndices.map((chunkIndex) => {
      const params = chunkParams[chunkIndex];
      if (params === undefined) throw new Error('chunk index out of range');
      const base = legacyChapterChunkQueryOptions(params, fetchChunk);
      const initialData = queryClient.getQueryData<TPage>(base.queryKey);
      return queryOptions({
        ...base,
        enabled,
        ...(initialData === undefined ? {} : { initialData }),
      });
    }),
  });

  const nextStates: Array<LegacyChapterChunkState<TPage>> = Array.from(
    { length: chunkParams.length },
    () => IDLE_CHUNK,
  );
  observedChunkIndices.forEach((chunkIndex, position) => {
    const query = observedQueries[position];
    if (!query) return;
    nextStates[chunkIndex] = {
      status:
        query.status === 'success'
          ? 'success'
          : query.status === 'error'
            ? 'error'
            : 'pending',
      isPending: query.status !== 'success' && query.status !== 'error',
      isFetching: query.isFetching,
      isError: query.isError,
      error: query.error,
      data: query.data,
    };
  });
  const [shared, setShared] = useState<{
    chunkStates: ReadonlyArray<LegacyChapterChunkState<TPage>>;
    chunkData: ReadonlyArray<TPage | undefined>;
  }>({ chunkStates: [], chunkData: [] });
  const previousStates = shared.chunkStates;
  const chunkStates =
    previousStates.length === nextStates.length &&
    nextStates.every((state, index) => {
      const before = previousStates[index];
      return before !== undefined && sameChunkState(before, state);
    })
      ? previousStates
      : nextStates.map((state, index) => {
          const before = previousStates[index];
          return before !== undefined && sameChunkState(before, state)
            ? before
            : state;
        });
  const nextChunkData = chunkStates.map((state) => state.data);
  const chunkData = shallowEqualArrays(shared.chunkData, nextChunkData)
    ? shared.chunkData
    : nextChunkData;
  if (chunkStates !== shared.chunkStates || chunkData !== shared.chunkData) {
    setShared({ chunkStates, chunkData });
  }

  return { chunkParams, chunkStates, chunkData };
}
